import { useState } from 'react';
import { MoreHorizontalIcon, PlusIcon } from 'lucide-react';

import { useServiceTypeTemplateCtx } from '@/app/(app)/settings/service_types/[id]/provider';
import { ButtonEmpty } from '@/components/atoms/button-empty';
import { PriceAmountInput } from '@/components/atoms/price-input';
import { ApiSchemas } from '@/components/providers/v1-api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

export function ServiceTypeTemplateDetails() {
  const { serviceTypeTemplate, update } = useServiceTypeTemplateCtx();

  return (
    <div className='flex w-full flex-col gap-8'>
      {/* Status Section */}
      <div className='flex flex-col'>
        <h2 className='pb-3 text-heading-md'>Status</h2>
        <div className='flex flex-col border-t-[0.5px]'>
          <div className='flex items-start gap-2 py-3'>
            <Switch
              checked={serviceTypeTemplate.status === 'ACTIVE'}
              onCheckedChange={(checked) => update({ status: checked ? 'ACTIVE' : 'INACTIVE' })}
            />
            <div className='flex flex-col gap-[3px] pt-0.5'>
              <Label className='leading-none'>Active Status</Label>
              <span className='text-sm text-muted-foreground'>Enable or disable this service type template.</span>
            </div>
          </div>
        </div>
      </div>

      {/* General Details Section */}
      <div className='flex flex-col gap-4'>
        <h2 className='text-heading-md'>General</h2>
        <div className='grid gap-x-2 gap-y-4 md:grid-cols-2'>
          <div className='space-y-2'>
            <Label>Name</Label>
            <Input value={serviceTypeTemplate.name} onChange={(e) => update({ name: e.target.value })} />
          </div>
          <div className='space-y-2'>
            <Label>Event Duration (mins)</Label>
            <EventDurationInput
              value={serviceTypeTemplate.event_duration_minutes}
              onChange={(value) => update({ event_duration_minutes: value })}
            />
          </div>
        </div>
        <div className='space-y-2'>
          <Label>Description</Label>
          <Textarea
            value={serviceTypeTemplate.description}
            onChange={(e) => update({ description: e.target.value })}
            rows={3}
          />
        </div>
      </div>

      {/* Pricing Section */}
      <ServiceTypePricing prices={serviceTypeTemplate.prices} updatePrices={(prices) => update({ prices })} />
    </div>
  );
}

type ApiPrice = ApiSchemas['ServiceTypeTemplatePrice'];
interface ServiceTypePricingProps {
  prices: ApiPrice[];
  updatePrices: (prices: ApiPrice[]) => void;
}

function ServiceTypePricing({ prices, updatePrices }: ServiceTypePricingProps) {
  const addPrice = () => {
    updatePrices([
      ...prices,
      {
        name: '',
        amount: 0,
        currency: 'USD',
        default: prices.length === 0,
      },
    ]);
  };

  const removePrice = (index: number) => {
    updatePrices(prices.filter((_, i) => i !== index));
  };

  const updatePrice = (index: number, updates: Partial<ApiPrice>) => {
    updatePrices(prices.map((price, i) => (i === index ? { ...price, ...updates } : price)));
  };

  const setDefaultPrice = (index: number) => {
    updatePrices(prices.map((price, i) => ({ ...price, is_default: i === index })));
  };

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center justify-between'>
        <h2 className='text-heading-md'>Pricing</h2>
        <Button variant='outline' size='sm' onClick={addPrice}>
          <PlusIcon className='h-4 w-4' />
          Add Price
        </Button>
      </div>
      {prices.length === 0 ? (
        <ButtonEmpty title='No pricing configured.' subtitle='Click to add.' onClick={addPrice} />
      ) : (
        <div className='space-y-4'>
          {prices.map((price, index) => (
            <PricingItem
              key={index}
              price={price}
              index={index}
              remove={() => removePrice(index)}
              update={(updates) => updatePrice(index, updates)}
              setDefault={() => setDefaultPrice(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface PricingItemProps {
  price: ApiPrice;
  remove: () => void;
  update: (v: Partial<ApiPrice>) => void;
  setDefault: () => void;
  index: number;
}

function PricingItem({ price, remove, update, setDefault, index }: PricingItemProps) {
  return (
    <div className='flex w-full flex-col gap-4 rounded-lg border p-6'>
      <div className='flex w-full items-center justify-between'>
        <div className='flex items-center gap-3'>
          <div className='text-base font-medium'>{price.name || 'Price Option ' + (index + 1)}</div>
          {price.default && <Badge variant='default'>Default</Badge>}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='outline' size='icon'>
              <MoreHorizontalIcon className='h-4 w-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={setDefault} disabled={price.default}>
              Set as default
            </DropdownMenuItem>
            <DropdownMenuItem variant='destructive' onClick={remove}>
              Remove
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className='grid gap-x-2 gap-y-4 md:grid-cols-2'>
        <div className='space-y-2'>
          <Label>Price Name</Label>
          <Input
            value={price.name}
            onChange={(e) => update({ name: e.target.value })}
            placeholder='e.g., Standard, Premium'
          />
        </div>
        <div className='space-y-2'>
          <Label>Amount</Label>
          <PriceAmountInput
            value={price.amount}
            onChange={(value) => update({ amount: value })}
            placeholder='0.00'
            className='w-full'
          />
        </div>
      </div>
    </div>
  );
}

interface EventDurationInputProps {
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  className?: string;
}

function EventDurationInput({ value, onChange, className }: EventDurationInputProps) {
  const [inputValue, setInputValue] = useState<string>(value?.toString() || '');

  const handleChange = (inputValue: string) => {
    // Allow empty string for clearing
    if (inputValue === '') {
      setInputValue('');
      onChange(null);
      return;
    }

    // Only allow integers with max 3 digits
    if (/^\d{1,3}$/.test(inputValue)) {
      setInputValue(inputValue);
      onChange(parseInt(inputValue, 10));
    }
  };

  return (
    <Input
      className={className}
      value={inputValue}
      onChange={(e) => handleChange(e.target.value)}
      placeholder='Enter duration in minutes'
    />
  );
}
